import React, { useEffect, useRef, useState } from 'react';
import { Button, Row, Col } from 'antd';
import { useParams } from 'react-router-dom';
import { fetchViewWorkOrderById } from '../../slices/work-order';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import logo from '../../Images/logo.svg';


const PrintWorkOrderFull = () => {
  const contentRef = useRef();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [workOrderData, setWorkOrderData] = useState(null);
  const { user: currentUser } = useSelector(state => state.auth);

  const fetchData = async () => {
    try {
      const response = await dispatch(fetchViewWorkOrderById({ id })).unwrap();
      setWorkOrderData(response.results.data[0]);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
    }
  };

  const toleranceOptions = [
    { value: 'Zero(0)', label: 'Zero(0)' },
    { value: '+-3%', label: '±3%' },
    { value: '+-5%', label: '±5%' },
    { value: '+-7%', label: '±7%' },
    { value: '+-10%', label: '±10%' },
    { value: '+3%', label: '+3%' },
    { value: '+5%', label: '+5%' },
    { value: '+7%', label: '+7%' },
    { value: '+10%', label: '+10%' },
    { value: '-3%', label: '-3%' },
    { value: '-5%', label: '-5%' },
    { value: '-7%', label: '-7%' },
    { value: '-10%', label: '-10%' },
  ];

  const surfaceFinishOptions = [
    { title: 'Mill Finish', key: 'mill_finish', subOption: [] },
    { title: 'Engineering', key: 'engineering', subOption: ['cutting', 'machinings', 'deburring'] },
    { title: 'Surface treatment', key: 'surface_treatment', subOption: ['anodising', 'powder_coating', 'pvdf'] },
    { title: 'Out Source', key: 'out_source', subOption: [] },
    { title: 'Laser marking', key: 'laser_marking', subOption: [] },
    { title: 'Thermal Break', key: 'thermal_break', subOption: [] },
  ];

  const selectedFinishes = workOrderData?.work_order_details?.flatMap(detail => detail.jobwork) || [];
 
  const matchedFinishes = surfaceFinishOptions.filter(option =>
    selectedFinishes.includes(option.title),
  );


  const surfaceFinishColumn = [
    {
      title: 'JOBWORK',
      align: 'center',
      width: '120px',
      children: matchedFinishes.map(finish => {
        const hasSubOptions = Array.isArray(finish.subOption) && finish.subOption.length > 0;

        return {
          title: finish.title,
          align: 'center',
          key: `${finish.key}_merged`,
          width: hasSubOptions ? finish.subOption.length * 120 + 'px' : '160px',
          ...(hasSubOptions
            ? {
              children: finish.subOption.map(sub => ({
                title: sub.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                key: `${finish.key}_${sub}`,
                align: 'center',
                width: '120px',
                render: (_, record) => {
                  // You can customize this logic as needed
                  return record[sub] || '-';
                },
              })),
            }
            : {
              render: (_, record) => {
                if (record.jobwork === finish.title) {
                  if (finish.title === 'Mill Finish') return '-';
                  const treatment = record.surface_treatment || '';
                  const description = record.description || '';
                  return `${treatment}${treatment && description ? ' / ' : ''}${description}`;
                }
                return '';
              },
            }),
        };
      }),
    },
  ];

  const dynamicFinishColumns = matchedFinishes
    .filter(finish => finish.title !== 'Mill Finish') // exclude Mill Finish
    .map(finish => ({
      title: `${finish.title} Rate`,
      dataIndex: `${finish.key}_rate`, // expected data key: e.g., anodize_rate
      key: `${finish.key}_rate`,
      align: 'center',
      width: 100,
      render: (_, record) => {
        // Only show the rate if the surface_finish matches this finish type
        return record.surface_finish === finish.att
          ? (record.finishing_price ?? '') // or use .toFixed(3) if needed
          : '';
      }
    }));

  useEffect(() => {
    fetchData();
  }, [id]);

  const columns = [
    {
      title: 'SR NO.',
      dataIndex: 'srNo',
      key: 'srNo',
      align: 'center',
      width: '30px',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'SECTION NO.',
      dataIndex: 'die_profile',
      key: 'die_profile',
      render: die_profile => <span>{die_profile?.die_number || ''}</span>,
      align: 'center',
      width: '60px',
    },
    {
      title: 'IMAGE',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      width: '90px',
      render: die_profile => {
        if (!die_profile?.die_diagram) return <span></span>;
        const fileUrl = die_profile.die_diagram;
        const isImage = /\.(jpeg|jpg|png|gif|bmp)$/i.test(fileUrl);
        const isPDF = /\.pdf$/i.test(fileUrl);
        return isImage ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={fileUrl}
              alt="Die Diagram"
              style={{
                maxWidth: '90px',
                maxHeight: '90px',
                objectFit: 'contain',
              }}
            />
          </div>
        ) : isPDF ? (
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            View PDF
          </a>
        ) : (
          <span>Unsupported Format</span>
        );
      },
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      width: '90px',
      render: (_, record) => {
        const dimensions =
          [
            record?.die_profile?.dimension1,
            record?.die_profile?.dimension2,
            record?.die_profile?.dimension3,
            record?.die_profile?.dimension4,
          ]
            .filter(dim => dim)
            .map(dim => Number(dim)?.toFixed(2))
            .join(' X ') + ' MM';
        return <span>{dimensions || ''}</span>;
      },
    },
    {
      title: 'WT RANGE',
      dataIndex: 'weight_range',
      key: 'weight_range',
      align: 'center',
      width: '60px',
    },
    {
      title: 'LENGTH',
      dataIndex: 'length',
      key: 'length',
      align: 'center',
      width: '40px',
    },
    {
      title: 'PIECES',
      children: [
        {
          title: 'Order',
          dataIndex: 'pieces',
          key: 'pieces',
          render: pieces => <span>{pieces || ''}</span>,
          align: 'center',
          width: '40px',
        },
        {
          title: 'Produced',
          dataIndex: 'packed_pieces',
          key: 'packed_pieces',
          align: 'center',
          width: '40px',
          render: packed_pieces => <span>{packed_pieces || ''}</span>,
        },
      ],
    },
    {
      title: 'WEIGHT (kg.)',
      children: [
        {
          title: 'Order',
          dataIndex: 'net_weight',
          key: 'net_weight',
          render: net_weight => <span>{net_weight || ''}</span>,
          align: 'center',
          width: '40px',
        },
        {
          title: 'Produced',
          dataIndex: 'packed_weight',
          key: 'packed_weight',
          align: 'center',
          width: '40px',
          render: packed_weight => <span>{packed_weight || ''}</span>,
        },
      ],
    },
    {
      title: 'ALLOY/TEMP-GRADE',
      dataIndex: 'alloy',
      key: 'alloy',
      render: (_, record) => {
        const alloyName = record?.alloy?.name || '';
        const alloyCode = record?.alloy?.color_code || '';
        const temperName = record?.temper?.name || '';
        const temperCode = record?.temper?.code || '';
        return (
          <span>
            {alloyName} ({alloyCode}) / {temperName} ({temperCode})
          </span>
        );
      },
      align: 'center',
      width: '80px',
    },
    ...surfaceFinishColumn,
  ];

  const columnsTwo = [
    {
      title: 'SR NO.',
      dataIndex: 'srNo',
      key: 'srNo',
      align: 'center',
      width: '30px',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'SECTION NO.',
      dataIndex: 'die_profile',
      key: 'die_profile',
      render: die_profile => <span>{die_profile?.die_number || ''}</span>,
      align: 'center',
      width: '60px',
    },
    {
      title: 'IMAGE',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      width: '90px',
      render: die_profile => {
        if (!die_profile?.die_diagram) return <span></span>;
        const fileUrl = die_profile.die_diagram;
        const isImage = /\.(jpeg|jpg|png|gif|bmp)$/i.test(fileUrl);
        const isPDF = /\.pdf$/i.test(fileUrl);
        return isImage ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={fileUrl}
              alt="Die Diagram"
              style={{
                maxWidth: '90px',
                maxHeight: '90px',
                objectFit: 'contain',
              }}
            />
          </div>
        ) : isPDF ? (
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            View PDF
          </a>
        ) : (
          <span>Unsupported Format</span>
        );
      },
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      width: '90px',
      render: (_, record) => {
        const dimensions =
          [
            record?.die_profile?.dimension1,
            record?.die_profile?.dimension2,
            record?.die_profile?.dimension3,
            record?.die_profile?.dimension4,
          ]
            .filter(dim => dim)
            .map(dim => Number(dim)?.toFixed(2))
            .join(' X ') + ' MM';
        return <span>{dimensions || ''}</span>;
      },
    },
    {
      title: 'WT RANGE',
      dataIndex: 'weight_range',
      key: 'weight_range',
      align: 'center',
      width: '60px',
    },
    {
      title: 'LENGTH',
      dataIndex: 'length',
      key: 'length',
      align: 'center',
      width: '40px',
    },
    {
      title: 'PIECES',
      children: [
        {
          title: 'Order',
          dataIndex: 'pieces',
          key: 'pieces',
          render: pieces => <span>{pieces || ''}</span>,
          align: 'center',
          width: '40px',
        },
        {
          title: 'Packed',
          dataIndex: 'packed_pieces',
          key: 'packed_pieces',
          align: 'center',
          width: '40px',
          render: packed_pieces => <span>{packed_pieces || ''}</span>,
        },
      ],
    },
    {
      title: 'WEIGHT (kg.)',
      children: [
        {
          title: 'Order',
          dataIndex: 'net_weight',
          key: 'net_weight',
          render: net_weight => <span>{net_weight || ''}</span>,
          align: 'center',
          width: '40px',
        },
        {
          title: 'Packed',
          dataIndex: 'packed_weight',
          key: 'packed_weight',
          align: 'center',
          width: '40px',
          render: packed_weight => <span>{packed_weight || ''}</span>,
        },
      ],
    },
    {
      title: 'ALLOY/TEMP-GRADE',
      dataIndex: 'alloy',
      key: 'alloy',
      render: (_, record) => {
        const alloyName = record?.alloy?.name || '';
        const alloyCode = record?.alloy?.color_code || '';
        const temperName = record?.temper?.name || '';
        const temperCode = record?.temper?.code || '';
        return (
          <span>
            {alloyName} ({alloyCode}) / {temperName} ({temperCode})
          </span>
        );
      },
      align: 'center',
      width: '80px',
    },
    ...surfaceFinishColumn,
  ];

  const isFixedPrice = workOrderData?.nalco_rate?.toLowerCase() === 'fixed';
  const columnsForAccountSales = [
    {
      title: 'SR NO.',
      dataIndex: 'srNo',
      key: 'srNo',
      align: 'center',
      width: '30px',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'SECTION NO.',
      dataIndex: 'die_profile',
      key: 'die_profile',
      render: die_profile => <span>{die_profile?.die_number || ''}</span>,
      align: 'center',
      width: '60px',
    },
    {
      title: 'IMAGE',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      width: '90px',
      render: die_profile => {
        if (!die_profile?.die_diagram) return <span></span>;
        const fileUrl = die_profile.die_diagram;
        const isImage = /\.(jpeg|jpg|png|gif|bmp)$/i.test(fileUrl);
        const isPDF = /\.pdf$/i.test(fileUrl);
        return isImage ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <img
              src={fileUrl}
              alt="Die Diagram"
              style={{
                maxWidth: '90px',
                maxHeight: '90px',
                objectFit: 'contain',
              }}
            />
          </div>
        ) : isPDF ? (
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            View PDF
          </a>
        ) : (
          <span>Unsupported Format</span>
        );
      },
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'die_profile',
      key: 'die_profile',
      align: 'center',
      width: '90px',
      render: (_, record) => {
        const dimensions =
          [
            record?.die_profile?.dimension1,
            record?.die_profile?.dimension2,
            record?.die_profile?.dimension3,
            record?.die_profile?.dimension4,
          ]
            .filter(dim => dim)
            .map(dim => Number(dim)?.toFixed(2))
            .join(' X ') + ' MM';
        return <span>{dimensions || ''}</span>;
      },
    },
    {
      title: 'WT RANGE',
      dataIndex: 'weight_range',
      key: 'weight_range',
      align: 'center',
      width: '60px',
    },
    {
      title: 'LENGTH',
      dataIndex: 'length',
      key: 'length',
      align: 'center',
      width: '40px',
    },
    {
      title: 'PIECES',
      dataIndex: 'pieces',
      key: 'pieces',
      align: 'center',
      width: '40px',
      render: pieces => <span>{pieces || ''}</span>,
    },
    {
      title: 'WEIGHT (kg.)',
      dataIndex: 'net_weight',
      key: 'net_weight',
      align: 'center',
      width: '40px',
      render: net_weight => <span>{net_weight || ''}</span>,
    },
    {
      title: 'ALLOY/TEMP-GRADE',
      dataIndex: 'alloy',
      key: 'alloy',
      align: 'center',
      width: '80px',
      render: (_, record) => {
        const alloyName = record?.alloy?.name || '';
        const alloyCode = record?.alloy?.color_code || '';
        const temperName = record?.temper?.name || '';
        const temperCode = record?.temper?.code || '';
        return (
          <span>
            {alloyName} ({alloyCode}) / {temperName} ({temperCode})
          </span>
        );
      },
    },
    ...surfaceFinishColumn,
    {
      title: 'Price/Kg',
      dataIndex: 'conversion',
      key: 'conversion',
      align: 'center',
      width: '50px',
      render: (_, record) => {
        const isFixed = workOrderData?.nalco_rate?.toLowerCase() === 'fixed';
        const isVariable =
          workOrderData?.nalco_rate?.toLowerCase() === 'variable';
        const conversion = record?.conversion ?? '0.00';
        if (isFixed)
          return <span>{`${record?.nalco_rate ?? '0.00'}+${conversion}`}</span>;
        if (isVariable) return <span>{`Variable+${conversion}`}</span>;
        return <span>0.00</span>;
      },
    },
    ...dynamicFinishColumns,
    {
      title: 'Packing Charges',
      dataIndex: 'packing_charges',
      key: 'packing_charges',
      align: 'center',
      width: '50px',
      render: (_, record) => <span>{record?.packing_charge || ''}</span>,
    },
    ...(isFixedPrice
      ? [
        {
          title: 'AMOUNT(Rs.)',
          dataIndex: 'amount',
          key: 'amount',
          align: 'center',
          width: '50px',
          render: (_, record) => (
            <span>{record?.total_basic_amount || ''}</span>
          ),
        },
        {
          title: 'IGST(18%)',
          dataIndex: 'gst',
          key: 'gst',
          align: 'center',
          width: '30px',
          render: (_, record) => <span>{record?.gst_amount || ''}</span>,
        },
        {
          title: 'TOTAL AMOUNT(Rs.)',
          dataIndex: 'total_amount',
          key: 'total_amount',
          align: 'center',
          width: '40px',
          render: (_, record) => <span>{record?.total_amount || ''}</span>,
        },
      ]
      : []),
  ];

  const printStyles = `
    @media print {
      * {
        box-sizing: border-box !important;
        margin: 0 !important;
        padding: 0 !important;
        color: #000000 !important;
        font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
         -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
      }
     
      html, body {
    width: 100%;
    height: auto;
    overflow: visible !important;
  }

      table {
  color: black;
  text-align: center;
  border-collapse: collapse;
}
table, th, td {
  text-align: center;
    border-collapse: collapse;
}
.table-bordered {
  border: 1px solid #dee2e6;
}
.table-bordered td, .table-bordered th {
  border: 1px solid #dee2e6;
}
.table-bordered td, .table-bordered thead th {
  border-bottom-width: 2px;
}
.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
}
.table-bordered th {
  border: 1px solid #dee2e6;
}
.table td, .table th {
  padding: .75rem;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}

      .print-area, .print-area * {
        visibility: visible;
      }
      .print-area {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        margin: 0 !important;
        padding: 5px !important;
      }
      .page-break {
        display: block !important;
        page-break-before: always;
        break-before: always;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .main-container {
       
        margin: 0 auto !important;
        padding: 0 !important;
      }
      .header-section {
        text-align: center;
        margin-bottom: 10px !important;
      }
      .header-title {
        font-size: 24px !important;
        font-weight: bold;
        margin: 0 !important;
      }
      .sub-header {
        font-size: 20px !important;
        font-weight: bold;
        margin: 0 !important;
        
      }
      .copy-label {
        float: right;
        font-size: 18px !important;
        font-weight: normal;
        margin: 0 !important;
      }
      .info-section {
        display: flex;
        justify-content: space-between;
        margin: 40px 0 !important;
        font-size: 16px !important;
      }
      .signature-section {
        display: flex;
        justify-content: space-between;
        margin-top: 80px !important;
        font-size: 15px !important;
      }
      .signature-section div {
        width: 25%;
        text-align: center;
      }
      .no-print {
        display: none !important;
      }
        .new-info{
          
           margin-top:20px !important;
        }
    }
  `;

  const commonStyles = `
  * {
        font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
      }
        body {
        font-size: 1rem !important;
           font-family:  -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
        }
    .print-container {
      // width: 800px;
      margin: 0 auto;
      padding: 10px;
      font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
    }
    .main-container {
      width: 100%;
      padding: 0;
    }
      table {
  color: black;
  text-align: center;
  border-collapse: collapse;
}
table, th, td {
  text-align: center;
    border-collapse: collapse;
}
.table-bordered {
  border: 1px solid #dee2e6;
}
.table-bordered td, .table-bordered th {
  border: 1px solid #dee2e6;
}
.table-bordered td, .table-bordered thead th {
  border-bottom-width: 2px;
}
.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
}
.table-bordered th {
  border: 1px solid #dee2e6;
}
.table td, .table th {
  padding: .75rem;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}
    .header-section {
      text-align: center;
      margin-bottom: 20px;
    }
    
    .sub-header {
      font-size: 24px;
      font-weight: bold;
      margin: 0;
      
    }
    .copy-label {
      float: right;
      font-size: 18px;
      font-weight: normal;
    }
    .info-section {
      display: flex;
      justify-content: space-between;
      margin: 20px 0;
      font-size: 16px;
    }
    .info-section div {
      font-size: 16px;
    }
    
    .signature-section {
      display: flex;
      justify-content: space-between;
      margin-top: 100px;
      font-size: 15px;
    }
    .signature-section div {
      width: 25%;
      text-align: center;
    }
    .page-break {
      display: none;
    }
  `;

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = printStyles + commonStyles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const renderCopy = (title, columns, data) => (
    <div className="main-container">
      {/* <div className="header-section">
        <div className="header-title" style={{marginTop: '40px'}}>
          Alux ERP
        </div>

        <div className="sub-header">WORK ORDER</div>
        <div>
          {' '}
          <span className="copy-label">{title}</span>
        </div>
      </div> */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '10px', marginTop: '5px' }}>
        <Col span={24} className="logo-section" style={{ textAlign: 'right' }}>
          <img src={logo} alt="Company Logo" style={{ maxWidth: '180px' }} />
        </Col>
      </Row>
      <Row justify="space-between" align="middle" style={{ marginBottom: '10px' }}>
        <Col span={24}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '2.75rem', color: '#000000', fontWeight: 500 }} className='main-heading'>WORK ORDER</h3>
            <span className="copy-label">{title}</span>
          </div>
        </Col>
      </Row>

      <div className="info-section" style={{ marginTop: '40px' }}>
        <div>
          <div>
            <strong>{workOrderData?.bill_to?.customer_name || ''}</strong>
            <br />
            {workOrderData?.bill_to?.factory_address?.shop ||
              workOrderData?.bill_to?.factory_address?.area ||
              workOrderData?.bill_to?.factory_address?.landmark ||
              workOrderData?.bill_to?.factory_address?.city ||
              workOrderData?.bill_to?.factory_address?.state ||
              workOrderData?.bill_to?.factory_address?.pin_code ? (
              <>
                {[
                  workOrderData?.bill_to?.factory_address?.shop,
                  workOrderData?.bill_to?.factory_address?.area,
                  workOrderData?.bill_to?.factory_address?.landmark,
                ]
                  .filter(Boolean)
                  .join(', ')}
                <br />
                {[
                  workOrderData?.bill_to?.factory_address?.city,
                  workOrderData?.bill_to?.factory_address?.state,
                  workOrderData?.bill_to?.factory_address?.pin_code,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </>
            ) : (
              ''
            )}
          </div>
          <div style={{ marginTop: '10px' }}>
            <strong>Delivery Party:</strong> <br />
            {workOrderData?.ship_to?.customer_name || ''}
          </div>
        </div>
        <div>
          <div>
            <strong>ORDER DATE:</strong>{' '}
            {workOrderData?.order_date
              ? dayjs(workOrderData.order_date).format('DD/MM/YYYY')
              : ''}
          </div>
          <div>
            <strong>ORDER NUMBER:</strong> {workOrderData?.order_no || ''}
          </div>
          <div>
            <strong>PURCHASE ORDER DATE:</strong>{' '}
            {workOrderData?.purchase_order_date
              ? dayjs(workOrderData.purchase_order_date).format('DD/MM/YYYY')
              : ''}
          </div>
          <div>
            <strong>PURCHASE ORDER NUMBER:</strong>{' '}
            {workOrderData?.purchase_order_no || ''}
          </div>
        </div>
      </div>

      <table style={{ marginTop: '15px', fontSize: '15px', width: "100%" }} className="table table-bordered">
        <thead>
          <tr>
            {columns.map((col, index) => {
              if (col.children) {
                // Parent column with nested children
                return (
                  <th key={index} colSpan={col.children.length} align="center">
                    {col.title}
                  </th>
                );
              } else {
                // Regular column with no children
                return (
                  <th key={index} rowSpan={2} align="center">
                    {col.title}
                  </th>
                );
              }
            })}
          </tr>
          <tr>
            {columns
              .filter(col => col.children)
              .flatMap(col =>
                col.children.map((child, i) => (
                  <th key={`${col.key}-${i}`} align="center">
                    {child.title}
                  </th>
                ))
              )}
          </tr>
        </thead>
        <tbody>
          {data?.map((row, index) => {
            if (title === 'ACCOUNT/SALES COPY') {
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row?.die_profile?.die_number}</td>
                  <td><img id="imglogo" alt="Profile Logo" src={row?.die_profile?.die_diagram} style={{ maxHeight: '90px', maxWidth: "90px" }} /></td>
                  <td> {[row.die_profile?.dimension1, row.die_profile.dimension2, row.die_profile.dimension3, row.die_profile.dimension4].filter(dim => dim).map(dim => Number(dim).toFixed(2)).join(' X ') + ' MM'}</td>
                  <td>{row?.weight_range}</td>
                  <td>{row?.length}</td>
                  <td>{row?.pieces}</td>
                  <td>{row?.net_weight}</td>
                  <td>{row?.alloy?.name} ({row?.alloy?.color_code}) / {row?.temper?.code} ({row?.temper?.name})</td>
                  {matchedFinishes.map((finish, colIndex) => {
                    const isMatch = row.surface_finish === finish.att;

                    let content = '';
                    if (isMatch) {
                      if (finish.att === 'Mill Finish') {
                        content = 'Mill Finish';
                      } else {
                        const treatment = row.surface_treatment || '';
                        const desc = row.description || '';
                        content = `${treatment}${treatment && desc ? ' / ' : ''}${desc}`;
                      }
                    }

                    return <td key={colIndex}>{content}</td>;
                  })}
                  <td>

                    {(() => {
                      const isFixed = workOrderData?.nalco_rate?.toLowerCase() === 'fixed';
                      const isVariable = workOrderData?.nalco_rate?.toLowerCase() === 'variable';
                      const conversion = row?.conversion ?? '0.00';

                      if (isFixed) {
                        return `${row?.nalco_rate ?? '0.00'}+${conversion}`;
                      }
                      if (isVariable) {
                        return `Variable+${conversion}`;
                      }
                      return '0.00';
                    })()}
                  </td>
                  {matchedFinishes
                    .filter(finish => finish.att !== 'Mill Finish') // ❌ exclude Mill Finish
                    .map((finish, colIndex) => {
                      const isMatch = row?.surface_finish === finish.att;

                      let content = '';
                      if (isMatch) {
                        const price = row?.finishing_price || '';
                        content = `${price}`;
                      }

                      return <td key={colIndex}>{content}</td>;
                    })}
                  <td>
                    {row?.packing_charge !== null ? row.packing_charge : 0.00}
                  </td>
                  {workOrderData?.nalco_rate?.toLowerCase() === 'fixed' ? <td>{row?.total_basic_amount}</td> : null}
                  {workOrderData?.nalco_rate?.toLowerCase() === 'fixed' ? <td>{row?.gst_amount}</td> : null}
                  {workOrderData?.nalco_rate?.toLowerCase() === 'fixed' ? <td>{row?.total_amount}</td> : null}
                </tr>
              );
            } else {
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row?.die_profile?.die_number}</td>
                  <td><img id="imglogo" alt="Profile Logo" src={row?.die_profile?.die_diagram} style={{ maxHeight: '90px', maxWidth: "90px" }} /></td>
                  <td> {[row.die_profile?.dimension1, row.die_profile.dimension2, row.die_profile.dimension3, row.die_profile.dimension4].filter(dim => dim).map(dim => Number(dim).toFixed(2)).join(' X ') + ' MM'}</td>
                  <td>{row?.weight_range}</td>
                  <td>{row?.length}</td>
                  <td>{row?.pieces}</td>
                  <td></td>
                  <td>{row?.min_weight}</td>
                  <td></td>
                  <td>{row?.alloy?.name} ({row?.alloy?.color_code}) / {row?.temper?.code} ({row?.temper?.name})</td>

                  {matchedFinishes.map((finish, colIndex) => {
                    const isMatch = row.surface_finish === finish.att;

                    let content = '';
                    if (isMatch) {
                      if (finish.att === 'Mill Finish') {
                        content = 'Mill Finish A';
                      } else {
                        const treatment = row.surface_treatment || '';
                        const desc = row.description || '';
                        content = `${treatment}${treatment && desc ? ' / ' : ''}${desc}`;
                      }
                    }

                    return <td key={colIndex}>{content}</td>;
                  })}

                </tr>
              );
            }
          })}
        </tbody>
        <tfoot>
          {(() => {
            const totals = {
              pieces: 0,
              net_weight: 0
            };
            data?.forEach(row => {
              totals.pieces += Number(row.pieces) || 0;
              totals.net_weight += Number(row.net_weight) || 0;
              totals.total_basic_amount += Number(row.total_basic_amount) || 0;
              totals.gst_amount += Number(row.gst_amount) || 0;
              totals.total_amount += Number(row.total_amount) || 0;
            });
            if (title === 'ACCOUNT/SALES COPY') {
              return (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center' }}><b>Total</b></td>
                  <td style={{ textAlign: 'center' }}><b>{totals?.pieces}</b></td>
                  <td style={{ textAlign: 'center' }}><b>{totals?.net_weight?.toFixed(3)}</b></td>
                  <td colSpan={
                    1 + matchedFinishes.length + 1 + matchedFinishes.filter(f => f.att !== 'Mill Finish').length + 1
                  }></td>
                  {workOrderData?.nalco_rate?.toLowerCase() === 'fixed' ? <td></td> : null}
                  {workOrderData?.nalco_rate?.toLowerCase() === 'fixed' ? <td></td> : null}
                  {workOrderData?.nalco_rate?.toLowerCase() === 'fixed' ? <td></td> : null}
                </tr>
              )
            } else {
              return (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center' }}><b>Total</b></td>
                  <td style={{ textAlign: 'center' }}><b>{totals?.pieces}</b></td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}><b>{totals?.net_weight?.toFixed(3)}</b></td>
                  <td></td>
                  <td></td>
                  <td colSpan={
                    1 + matchedFinishes.length + 1 + matchedFinishes.filter(f => f.att !== 'Mill Finish').length + 1
                  }></td>
                </tr>
              );
            }
          })()}
        </tfoot>
      </table>

      <div className="info-section">
        <div>
          <div>
            <strong>SUPPLY TOLERANCE:</strong>{' '}
            {toleranceOptions.find(
              opt => opt.value === workOrderData?.tolerance,
            )?.label || ''}
          </div>
          <div>
            <strong>DELIVERY DT.:</strong>{' '}
            {workOrderData?.delivery_date
              ? dayjs(workOrderData.delivery_date).format('DD/MM/YYYY')
              : ''}
          </div>
          <div>
            <strong>QUANTITY CONTROL:</strong> As Per INDIAN STANDARDS (IS)
          </div>
          <div>
            <strong>REMARKS:</strong> <br />
            {workOrderData?.remarks || ''}
          </div>
        </div>
        <div>
          <div>
            <strong>PACKING TYPE:</strong>{' '}
            {workOrderData?.packing_type?.length
              ? workOrderData.packing_type.join(', ')
              : ''}
          </div>
        </div>
      </div>

      <div className="signature-section">
        <div>
          <strong>PROCESSED BY</strong>
          <br />
          (Sales)
        </div>
        <div>
          <strong>CHECKED BY</strong>
          <br />
          (Design Dept.)
        </div>
        <div>
          <strong>APPROVED BY</strong>
          <br />
          (Sales Head)
        </div>
        <div>
          <strong>APPROVED BY</strong>
          <br />
          (Director)
        </div>
      </div>
    </div>
  );

  return (
    <div ref={contentRef} className="print-container print-area">
      {currentUser.permissions.permissions.workorder.includes(
        'print_production_copy',
      ) &&
        renderCopy(
          'PRODUCTION COPY',
          columns,
          workOrderData?.work_order_details,
        )}

      {currentUser.permissions.permissions.workorder.includes(
        'print_packing_copy',
      ) && (
          <>
            <div className="page-break" />
            {renderCopy(
              'PACKING COPY',
              columnsTwo,
              workOrderData?.work_order_details,
            )}
          </>
        )}

      {currentUser.permissions.permissions.workorder.includes(
        'print_account_sales_copy',
      ) && (
          <>
            <div className="page-break" />
            {renderCopy(
              'ACCOUNT/SALES COPY',
              columnsForAccountSales,
              workOrderData?.work_order_details,
            )}
          </>
        )}

      <div className="text-center no-print" style={{ marginTop: '20px' }}>
        <Button type="primary" onClick={handlePrint}>
          Print
        </Button>
      </div>
    </div>
  );
};

export default PrintWorkOrderFull;